import React, { useState, useEffect } from 'react';
import { CheckCircle2, X, Clock, CloudRain, Award, Shield, Star } from 'lucide-react';
import SEO from '../../components/SEO';
import CheckoutButton from '../../components/CheckoutButton';

const RoofingOffer = () => {
  const [timeLeft, setTimeLeft] = useState({ hours: 23, minutes: 47, seconds: 0 });
  const [spotsLeft] = useState(6);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { hours: prev.hours, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <SEO 
        title="Roofing Companies: Free Lifetime Upgrade - Stop Losing £47K/Year"
        description="Get every storm lead. Never miss another insurance job. Hero Domination features at Hero Starter price. Forever."
      />
      
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#1a1f3a] to-[#0A0E27]">
        <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white py-3 text-center font-bold sticky top-0 z-50 shadow-lg">
          <div className="container mx-auto px-4 flex items-center justify-center space-x-4">
            <CloudRain className="animate-pulse" size={20} />
            <span>STORM SEASON OFFER: {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s</span>
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm">Only {spotsLeft} roofing companies left</span>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12 max-w-5xl">
          <div className="text-center mb-12">
            <div className="inline-block bg-red-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
              ⚠️ STORM CHASERS & ROOFING COMPANIES ONLY
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
              A Storm Just Hit Your Area...<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">Why Is Your Competitor's Phone Ringing And Yours Isn't?</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              <strong className="text-white">FREE £8,400/Year Upgrade - 6 Roofing Companies Only</strong><br />
              Capture Every Storm Lead. Convert 67% More Insurance Jobs. Dominate Your Market.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur rounded-2xl p-8 mb-12 border border-white/10">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">
              The Brutal Truth About Storm Season...
            </h2>
            
            <div className="space-y-4">
              {[
                "Storm hits Saturday night. By Monday morning, 347 homeowners need roofs. You got 12 calls. Your competitor got 89. Why?",
                "You left a £14,500 quote under their door. They never called back. You never followed up. They hired someone else.",
                "Insurance adjuster asked for proof of work. You have 9 Google reviews. Competitor has 247. Guess who got the job?",
                "You're juggling 23 jobs. Forgot to order materials for the Thompson job. Crew showed up. Nothing to do. £1,840 wasted.",
                "It's December. Storm season is over. You made good money but have ZERO maintenance contracts for spring. Bank account drops. Panic sets in.",
                "Customer called about hail damage. You were on a roof. Didn't answer. They called 3 more roofers. Someone DID answer. Lost: £18,900 insurance job."
              ].map((problem, i) => (
                <div key={i} className="flex items-start space-x-3 bg-red-900/20 p-4 rounded-lg border border-red-500/30">
                  <X className="text-red-500 flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-200">{problem}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-2xl text-orange-400 font-bold mb-2">
                Storm season should make you rich.
              </p>
              <p className="text-gray-300 text-lg">
                Instead, you're losing <strong className="text-white">£47,200 in missed opportunities</strong> every single storm.<br />
                Your competitors aren't better roofers. They just have better systems.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 border-2 border-orange-500 rounded-2xl p-8 mb-12">
            <div className="text-center mb-8">
              <Award className="mx-auto text-orange-400 mb-4" size={48} />
              <h2 className="text-4xl font-black text-white mb-4">
                What If You Captured EVERY Storm Lead?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "Storm Tracking + Instant Response",
                  desc: "Storm hits. System automatically texts every homeowner in affected zip code: 'Free roof inspection'. 68% book within 2 hours."
                },
                {
                  title: "Insurance Documentation Automation",
                  desc: "System collects photos, generates professional reports, tracks adjuster notes. You look 10x more professional than competitors."
                },
                {
                  title: "Follow-Up That Never Stops",
                  desc: "Left a quote? System follows up via text, email, and ringless voicemail for 30 days. Your close rate goes from 31% to 67%."
                },
                {
                  title: "Reviews Flood In Automatically",
                  desc: "Finish a roof. System texts homeowner + insurance adjuster asking for reviews. 71% leave 5-stars. Referrals skyrocket."
                },
                {
                  title: "Maintenance Contracts = Year-Round Income",
                  desc: "After storm jobs, system auto-enrolls customers in annual inspection program. You fill off-season with £497 inspections."
                },
                {
                  title: "Crew Management That Actually Works",
                  desc: "Automated schedules, material orders, customer updates. No more chaos. Crews show up with everything they need."
                }
              ].map((benefit, i) => (
                <div key={i} className="bg-white/10 backdrop-blur rounded-xl p-6 border border-orange-500/30">
                  <CheckCircle2 className="text-green-400 mb-3" size={32} />
                  <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-300">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold text-white text-center mb-8">
              Real Roofing Companies, Real Revenue
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  name: "Jake Morrison",
                  business: "Morrison Roofing, Oklahoma",
                  result: "£247K in 3 months (storm season)",
                  quote: "Last storm season I did 47 roofs. This year? 183. The automated storm lead system is crazy. I can't install roofs fast enough."
                },
                {
                  name: "Amanda Torres",
                  business: "Torres Roofing & Restoration",
                  result: "89% close rate on insurance jobs",
                  quote: "The insurance documentation automation makes us look like the most professional company in town. Adjusters LOVE working with us now."
                },
                {
                  name: "Marcus Johnson",
                  business: "Johnson Exterior Solutions",
                  result: "£84K off-season revenue",
                  quote: "The maintenance contract automation changed my life. I used to scramble for work in winter. Now I have 247 inspection contracts. Year-round income."
                }
              ].map((testimonial, i) => (
                <div key={i} className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={16} />
                    ))}
                  </div>
                  <p className="text-gray-200 mb-4 italic">"{testimonial.quote}"</p>
                  <div className="border-t border-white/10 pt-4">
                    <p className="font-bold text-white">{testimonial.name}</p>
                    <p className="text-sm text-gray-400">{testimonial.business}</p>
                    <p className="text-orange-400 font-bold mt-2">{testimonial.result}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 border-4 border-green-500 rounded-2xl p-8 mb-12">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-black text-white mb-4">
                The Offer: Save £8,400/Year Forever
              </h2>
              <p className="text-xl text-gray-200">
                {spotsLeft} roofing companies only. Expires in {timeLeft.hours}h {timeLeft.minutes}m
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-2xl font-bold text-white mb-4">Normal Price</h3>
                <p className="text-5xl font-black text-white mb-4">£297<span className="text-xl text-gray-400">/mo</span></p>
                <p className="text-gray-400">Hero Starter features only</p>
              </div>

              <div className="bg-gradient-to-br from-orange-500/30 to-red-500/30 rounded-xl p-6 border-4 border-orange-500 relative">
                <div className="absolute -top-3 -right-3 bg-green-600 text-white px-4 py-1 rounded-full text-sm font-bold animate-pulse">
                  YOUR LIFETIME PRICE
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Hero Domination</h3>
                <p className="text-5xl font-black text-white mb-4">£297<span className="text-xl text-gray-400">/mo</span></p>
                <p className="text-orange-400 font-bold mb-4">Get £997/mo features. Pay £297/mo. Forever.</p>
                <ul className="space-y-2">
                  {["50 users", "Unlimited contacts", "Storm lead automation", "Insurance doc system", "Priority support"].map((f, i) => (
                    <li key={i} className="flex items-center text-white">
                      <CheckCircle2 className="text-green-400 mr-2" size={18} />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="text-center">
              <CheckoutButton 
                plan="STARTER"
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white text-2xl px-12 py-6 rounded-full font-black shadow-2xl transform hover:scale-105 transition-all mb-4"
              >
                CLAIM MY LIFETIME UPGRADE - £5 Trial
              </CheckoutButton>
              <p className="text-gray-300 text-sm">14-day trial, then £297/mo with Domination features forever</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl p-8 text-center">
            <h2 className="text-4xl font-black text-white mb-4">
              Next Storm Is Coming. Will You Be Ready?
            </h2>
            <p className="text-xl text-white mb-6">
              {spotsLeft} spots left. After that, you pay full price like everyone else.
            </p>
            <CheckoutButton 
              plan="STARTER"
              className="bg-white text-red-600 text-2xl px-12 py-6 rounded-full font-black shadow-2xl transform hover:scale-105 transition-all hover:bg-gray-100"
            >
              YES! UPGRADE ME NOW
            </CheckoutButton>
          </div>

        </div>
      </div>
    </>
  );
};

export default RoofingOffer;